import {
  parseFileResourceName,
  getFileDisplayName,
} from '@/shared/utils/file-utils';
import { parseSizeBytes } from '@/shared/utils/format-bytes';
import { useFileMetadata } from './useFileMetadata';

/**
 * Resolves a Bishop file resource path to a human-readable display name.
 *
 * Encapsulates the three-step pattern used across many file-display components:
 *   1. parseFileResourceName  — extract org / workspace / file IDs
 *   2. useFileMetadata        — fetch canonical metadata from Bishop
 *   3. getFileDisplayName     — strip the storage-ID prefix from the filename
 *
 * When `resourcePath` is null, undefined, or cannot be parsed (e.g. an inline
 * file has no remote path), the hook is effectively disabled and returns the
 * provided `fallback` immediately without making any network requests.
 *
 * React Query deduplicates metadata fetches across the component tree so
 * displaying the same file in multiple places never results in duplicate calls.
 *
 * @param resourcePath - Full Bishop resource path (e.g. "organizations/acme/files/abc--doc.pdf")
 *   or null/undefined when no remote path is available.
 * @param fallback - Display name to use while loading or when the resource path
 *   cannot be resolved (e.g. extracted local name, raw filename).
 * @returns `{ displayName, sizeBytes, mimeType, isLoading }` where `displayName`
 *   is the resolved human-readable name, `sizeBytes` is the file's size when
 *   metadata supplies one (null otherwise), `mimeType` is the content type
 *   metadata reports (undefined otherwise), and `isLoading` is true while the
 *   metadata fetch is in flight.
 *
 * @example
 * // In a component that receives a Bishop resource path:
 * const { displayName, isLoading } = useResolvedFileDisplayName(
 *   file.resourceName,
 *   file.name,
 * );
 *
 * // For an inline file (no remote path):
 * const { displayName } = useResolvedFileDisplayName(undefined, inlineName);
 * // → { displayName: inlineName, isLoading: false }
 */
export function useResolvedFileDisplayName(
  resourcePath: string | null | undefined,
  fallback: string,
): {
  displayName: string;
  sizeBytes: number | null;
  mimeType: string | undefined;
  isLoading: boolean;
} {
  const parsedFile = resourcePath ? parseFileResourceName(resourcePath) : null;
  const { data: fileMetadata, isLoading } = useFileMetadata(parsedFile);

  const displayName = fileMetadata?.data?.filename
    ? getFileDisplayName(fileMetadata.data.filename)
    : fallback;

  return {
    displayName,
    sizeBytes: parseSizeBytes(fileMetadata?.data?.size_bytes),
    mimeType: fileMetadata?.data?.mime_type,
    isLoading,
  };
}
